import { useState, useEffect, useCallback } from "react";
import { Button, Icon } from "@legal-500/legal500-design-system";
import type { Entity } from "../../components/EntityPicker";
import { type OfficeContactPair } from "../../components/EliteMetadataConfiguration";
import type { SubscriptionWithFeatures } from "../../api/subscriptions.service";
import { editorialCategories } from "../../api/editorial-categories.service";
import styles from "./OnboardingTab.module.css";
import { SearchableSelect } from "@/components/Form/SearchableSelect";
import { RadioCard } from "@/components/RadioCard/RadioCard";
import { LANGUAGE_LABELS, LANGUAGES, SITES } from "@/constants/features";
import { MetadataFieldsEditor, extractEntityNoun } from "./MetadataFieldsEditor";
import { formatMetadataKey, useSubscriptionMetadataEntities } from "./hooks";

type OnboardingTabProps = {
  subscription: SubscriptionWithFeatures;
  onComplete: (data: OnboardingData) => void;
  onCancel: () => void;
  apiError?: string | null;
};

export type OnboardingData = {
  siteStructureId?: string;
  site?: string | null;
  language?: string | null;
  metadata: {
    contact_ids?: (number | string)[];
    editorial_category_ids?: (number | string)[];
    office_contact_pairs?: OfficeContactPair[];
  };
};

function formatMetadataValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "N/A";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

export function OnboardingTab({
  subscription,
  onComplete,
  onCancel,
  apiError,
}: OnboardingTabProps) {
  const [placementLocations, setPlacementLocations] = useState<Entity[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [locationsError, setLocationsError] = useState<string | null>(null);
  const [siteStructureId, setSiteStructureId] = useState<string | null>(null);
  const [site, setSite] = useState<string | null>(null);
  const [language, setLanguage] = useState<string | null>(null);
  const [metadataSelections, setMetadataSelections] = useState<Record<string, (number | string)[]>>(
    {},
  );
  const { entityOptions, entityErrors, isLoadingEntities, fieldLabel } =
    useSubscriptionMetadataEntities(subscription);
  const [validationError, setValidationError] = useState<string | null>(null);

  // State for office_contact_pairs - managed by EliteMetadataConfiguration
  const [officeContactPairs, setOfficeContactPairs] = useState<OfficeContactPair[]>([]);

  useEffect(() => {
    const fetchPlacementLocations = async () => {
      setIsLoadingLocations(true);
      setLocationsError(null);
      try {
        const categories = await editorialCategories.getAll();
        const options = categories.map(cat => ({
          value: cat.id,
          label: cat.name,
        }));
        setPlacementLocations(options);
      } catch (error) {
        setLocationsError("Failed to load placement locations");
        console.error("Error fetching placement locations:", error);
      } finally {
        setIsLoadingLocations(false);
      }
    };

    fetchPlacementLocations();
  }, []);

  const needsPlacement = subscription.siteStructureId === null;
  // Handler for office_contact_pairs from EliteMetadataConfiguration
  const handleEliteChange = useCallback((pairs: OfficeContactPair[]) => {
    setOfficeContactPairs(pairs);
  }, []);

  const metadataQuantities = subscription.package?.metadataQuantities || {};

  const customMetadata = subscription.metadata
    ? Object.entries(subscription.metadata).filter(
        ([key]) => !["contact_ids", "editorial_category_ids", "office_contact_pairs"].includes(key),
      )
    : [];

  const hasCustomMetadata = customMetadata.length > 0;

  const featureHelpTexts =
    subscription.package?.features
      ?.filter(feature => feature.entity_id_help_text)
      .map(feature => ({
        label: feature.entity_id_label || feature.name,
        helpText: feature.entity_id_help_text!,
      })) || [];

  const hasFeatureHelp = featureHelpTexts.length > 0;
  const showOnboardingInfo = hasCustomMetadata || hasFeatureHelp;

  const metadataQuantitiesEntries = Object.entries(metadataQuantities);

  const getValidationErrors = () => {
    const errors: string[] = [];
    if (needsPlacement && !siteStructureId) {
      errors.push("Please select a placement location");
    }

    // Validate each metadata quantity requirement
    for (const [key, requiredCount] of metadataQuantitiesEntries) {
      // Special handling for office_contact_pairs
      if (key === "office_contact_pairs") {
        const validPairs = officeContactPairs.filter(
          pair => pair.office_id > 0 && pair.contact_ids.length > 0,
        );
        // requiredCount of 0 means optional - don't require any selections
        // But still validate incomplete pairs if user started selecting
        if (requiredCount > 0 && validPairs.length === 0) {
          errors.push("Please select at least one office with contacts");
        }
        // Check for offices without contacts (warn even if optional)
        const incompletePairs = officeContactPairs.filter(
          pair => pair.office_id > 0 && pair.contact_ids.length === 0,
        );
        if (incompletePairs.length > 0) {
          errors.push("Please select contacts for all offices");
        }
        continue;
      }

      const selectedCount = metadataSelections[key]?.length || 0;
      const label = fieldLabel(key);
      const noun = extractEntityNoun(label);
      if (key === "publication_edition_ids") {
        if (selectedCount < 1) {
          errors.push(`Please select at least one ${noun}`);
        }
      } else if (selectedCount !== requiredCount) {
        errors.push(
          `Please select exactly ${requiredCount} ${noun}${requiredCount !== 1 ? "s" : ""}`,
        );
      }
    }

    return errors;
  };

  const handleSubmit = () => {
    const errors = getValidationErrors();

    if (errors.length > 0) {
      setValidationError(errors.length === 1 ? errors[0] : errors.join("|"));
      return;
    }

    setValidationError(null);

    const data: OnboardingData = {
      metadata: {
        ...metadataSelections,
      },
    };

    if (needsPlacement) {
      data.siteStructureId = siteStructureId || undefined;
      data.site = site;
      data.language = language;
    }

    // Add office_contact_pairs if present
    if (officeContactPairs.length > 0) {
      const validPairs = officeContactPairs.filter(
        pair => pair.office_id > 0 && pair.contact_ids.length > 0,
      );
      if (validPairs.length > 0) {
        data.metadata.office_contact_pairs = validPairs;
      }
    }

    onComplete(data);
  };

  const displayError = apiError || validationError;
  const displayErrors = displayError ? displayError.split("|") : [];

  return (
    <div className={styles.container}>
      {showOnboardingInfo && (
        <section className={styles.section}>
          <h3 className={styles.sectionHeading}>Onboarding Information</h3>
          <table className={styles.detailTable}>
            <tbody>
              {customMetadata.map(([key, value]) => (
                <tr key={key}>
                  <th className={styles.detailLabel}>{formatMetadataKey(key)}</th>
                  <td className={styles.detailValue}>{formatMetadataValue(value)}</td>
                </tr>
              ))}

              {featureHelpTexts.map((item, index) => (
                <tr key={index}>
                  <th className={styles.detailLabel}>{item.label}</th>
                  <td className={styles.detailValue}>{item.helpText}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section className={styles.section}>
        <h3 className={styles.sectionHeading}>Package</h3>
        <div className={styles.packageInfo}>
          <div className={styles.packageDetail}>
            <span className={styles.packageLabel}>Product Code:</span>
            <span className={styles.packageValue}>
              {subscription.package?.productCode || "N/A"}
            </span>
          </div>
          <div className={styles.packageDetail}>
            <span className={styles.packageLabel}>Version:</span>
            <span className={styles.packageValue}>v{subscription.package?.version || "1"}</span>
          </div>
        </div>
        {metadataQuantitiesEntries.length > 0 && (
          <div className={styles.requirementsInfo}>
            <p className={styles.requirementsLabel}>Selection Requirements:</p>
            <ul className={styles.requirementsList}>
              {metadataQuantitiesEntries.map(([key, quantity]) => (
                <li key={key}>
                  {fieldLabel(key)}: {key === "publication_edition_ids" ? "at least 1" : quantity}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {needsPlacement && (
        <>
          <section className={styles.section}>
            <h3 className={styles.sectionHeading}>Placement Location</h3>
            <div className={styles.sectionContent}>
              {isLoadingLocations ? (
                <div className={styles.loadingState}>Loading placement locations...</div>
              ) : locationsError ? (
                <div className={styles.errorState}>{locationsError}</div>
              ) : (
                <SearchableSelect
                  name="siteStructure"
                  label="Placement Location"
                  required
                  value={siteStructureId}
                  options={placementLocations}
                  placeholder="Select a placement location..."
                  onChange={value => setSiteStructureId(value as string | null)}
                  disabled={isLoadingLocations}
                />
              )}
            </div>
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionHeading}>Placement Configuration</h3>
            <div className={styles.sectionContent}>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Site <span className={styles.required}>*</span>
                </label>
                <div className={styles.radioGrid}>
                  <RadioCard
                    name="site"
                    value={null}
                    label="All Sites"
                    description="Apply to all available sites"
                    checked={site === null}
                    onChange={setSite}
                    compact={true}
                  />
                  {SITES.map(siteOption => (
                    <RadioCard
                      key={siteOption}
                      name="site"
                      value={siteOption}
                      label={siteOption}
                      checked={site === siteOption}
                      onChange={setSite}
                      compact={true}
                    />
                  ))}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Language <span className={styles.required}>*</span>
                </label>
                <div className={styles.radioGrid}>
                  <RadioCard
                    name="language"
                    value={null}
                    label="All Languages"
                    description="Apply to all available languages"
                    checked={language === null}
                    onChange={setLanguage}
                    compact={true}
                  />
                  {LANGUAGES.map(lang => (
                    <RadioCard
                      key={lang}
                      name="language"
                      value={lang}
                      label={LANGUAGE_LABELS[lang]}
                      checked={language === lang}
                      onChange={setLanguage}
                      compact={true}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      <MetadataFieldsEditor
        firmId={subscription.firmId}
        metadataQuantities={metadataQuantities}
        entityOptions={entityOptions}
        entityErrors={entityErrors}
        isLoadingEntities={isLoadingEntities}
        fieldLabel={fieldLabel}
        selections={metadataSelections}
        onSelectionsChange={(key, values) =>
          setMetadataSelections(prev => ({ ...prev, [key]: values }))
        }
        officeContactPairs={
          (subscription.metadata?.office_contact_pairs as OfficeContactPair[]) || []
        }
        onOfficeContactPairsChange={handleEliteChange}
        missingContactOffices={
          (subscription.metadata?.missing_contact_offices as Array<{
            office_id: number;
            city?: string | null;
          }>) || []
        }
      />

      {displayError && (
        <div className={styles.errorBanner}>
          <Icon icon="exclamation-circle" size="small" className={styles.errorIcon} />
          {displayErrors.length === 1 ? (
            <span className={styles.errorText}>{displayErrors[0]}</span>
          ) : (
            <div className={styles.errorText}>
              <ul className={styles.errorList}>
                {displayErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className={styles.actions}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmit}>Save Configuration</Button>
      </div>
    </div>
  );
}
